import { useState } from "react";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import { useStoreQuery, queries, mutations, type ExerciseKind, type SessionExercise, type GeneratedSession, type MovementProfile, type SeasonState } from "@/lib/store";
import SeasonChip from "@/components/mnf/SeasonChip";

/* ---------------------------- Workout definition --------------------------- */

const WORKOUT = {
  id: "strength-grace-lower",
  title: "Strength & Grace · Lower",
  focus: "Lower body & core",
  durationMin: 32,
  movements: 5,
};

type Exercise = {
  id: string;
  name: string;
  kind: ExerciseKind;
  sets: number;
  prescribedReps: number | null; // null for hold
  prescribedHoldSec: number | null;
  prescribedWeight: number | null; // pounds; null for BW or Hold
  note: string;
};

const exercises: Exercise[] = [
  { id: "goblet-squat",      name: "Goblet squat",      kind: "weighted",   sets: 4, prescribedReps: 10, prescribedHoldSec: null, prescribedWeight: 25, note: "Heels grounded, chest tall" },
  { id: "romanian-deadlift", name: "Romanian deadlift", kind: "weighted",   sets: 4, prescribedReps: 8,  prescribedHoldSec: null, prescribedWeight: 40, note: "Hips back, soft knees" },
  { id: "single-arm-row",    name: "Single-arm row",    kind: "weighted",   sets: 3, prescribedReps: 12, prescribedHoldSec: null, prescribedWeight: 18, note: "Brace through your core" },
  { id: "glute-bridge-hold", name: "Glute bridge hold", kind: "hold",       sets: 3, prescribedReps: null, prescribedHoldSec: 30, prescribedWeight: null, note: "Squeeze for one prayer" },
  { id: "bird-dog",          name: "Bird dog",          kind: "bodyweight", sets: 3, prescribedReps: 10, prescribedHoldSec: null, prescribedWeight: null, note: "Slow, intentional" },
];

const recentDays = [
  { d: "M", done: true,  intensity: 0.8 },
  { d: "T", done: true,  intensity: 0.6 },
  { d: "W", done: false, intensity: 0   },
  { d: "T", done: true,  intensity: 0.9 },
  { d: "F", done: false, intensity: 0   },
  { d: "S", done: true,  intensity: 0.5 },
  { d: "S", done: false, intensity: 0   },
];

export default function Move() {
  const [active, setActive] = useState(0);
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account: this page WRITES (toggleSaveWorkout, and set
  // logging inside ExerciseRow/SetRow). See the early return below — it makes
  // both writes unreachable while the identity is unresolved.
  const userId = me?.id ?? null;
  const setsToday = useStoreQuery(() => (userId ? queries.setsLoggedToday(userId, WORKOUT.id) : []));
  const isSaved = useStoreQuery(() => (userId ? queries.isWorkoutSaved(userId, WORKOUT.id) : false));
  const savedCount = useStoreQuery(() => (userId ? queries.savedWorkoutsForUser(userId).length : 0));

  const season = useStoreQuery(() => (userId ? queries.seasonState(userId) : null));

  // NASM/GGS personalization
  const todayIso = new Date().toISOString();
  const personalizedSession = useStoreQuery(() => (userId ? queries.todaysSession(userId, todayIso) : null));
  const mesocycle = useStoreQuery(() => (userId ? queries.mesocycleSummary(userId) : null));
  const profile = useStoreQuery(() => (userId ? queries.movementProfile(userId) : null));

  const completedExercises = exercises.filter(
    (ex) => setsToday.filter((s) => s.exerciseId === ex.id).length >= ex.sets,
  );

  // Season-aware movement gating
  const isPregnant = season?.kind === "pregnant";
  const isPostpartum = season?.kind === "postpartum";
  const isNursing = season?.kind === "nursing";
  const blockedByClearance = isPostpartum && !season?.clearedForMovement;

  const toggleFavorite = () => {
    if (!userId) return;
    mutations.toggleSaveWorkout({
      userId,
      workoutId: WORKOUT.id,
      title: WORKOUT.title,
      focus: WORKOUT.focus,
      durationMin: WORKOUT.durationMin,
      movements: WORKOUT.movements,
    });
  };

  const todayDate = new Date();
  const dayName = todayDate.toLocaleDateString("en-US", { weekday: "long" });
  const sessionFocus = personalizedSession?.focus ?? "Rest day";
  const headerEyebrow = `${dayName} \u00b7 ${sessionFocus}`;
  const headerDuration = personalizedSession?.durationMin ?? 0;
  const headerKcal = headerDuration ? Math.round(headerDuration * 7.5) : 0;
  const headerMovements = personalizedSession?.exercises.length ?? 0;

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see today's session, sister.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScreenHeader
        variant="forest"
        eyebrow={headerEyebrow}
        title="Strength & Grace"
        subtitle={
          <>
            "She girds herself with strength, and strengthens her arms."
            <span style={{ color: "rgba(229,185,151,0.95)" }}> · Proverbs 31:17</span>
          </>
        }
        trailing={
          <div className="flex items-center gap-2">
            <button
              onClick={toggleFavorite}
              className="flex items-center justify-center hover-elevate"
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: isSaved ? "rgba(160,120,32,0.22)" : "rgba(245,240,232,0.12)",
                color: isSaved ? "#E5B997" : "#F5F0E8",
                border: `1px solid ${isSaved ? "rgba(229,185,151,0.55)" : "rgba(245,240,232,0.18)"}`,
              }}
              aria-label={isSaved ? "Remove from your library" : "Save to your library"}
              data-testid="button-favorite-workout"
            >
              <Icon name={isSaved ? "bookmark" : "bookmark_border"} size={18} filled={isSaved} />
            </button>
            <Link
              href="/move/library"
              className="no-underline flex items-center justify-center hover-elevate"
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "rgba(245,240,232,0.12)", color: "#F5F0E8",
                border: "1px solid rgba(245,240,232,0.18)",
              }}
              aria-label="Your saved workouts"
              data-testid="link-library"
            >
              <Icon name="collections_bookmark" size={18} />
            </Link>
          </div>
        }
      >
        {personalizedSession ? (
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1.5">
              <Icon name="schedule" size={14} color="rgba(245,240,232,0.85)" />
              <span className="mnf-body text-xs" style={{ color: "rgba(245,240,232,0.9)" }}>{headerDuration} min</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="local_fire_department" size={14} color="rgba(229,185,151,0.95)" />
              <span className="mnf-body text-xs" style={{ color: "rgba(245,240,232,0.9)" }}>~ {headerKcal} kcal</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="fitness_center" size={14} color="rgba(245,240,232,0.85)" />
              <span className="mnf-body text-xs" style={{ color: "rgba(245,240,232,0.9)" }}>{headerMovements} movements</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 mt-2">
            <Icon name="self_care" size={14} color="rgba(229,185,151,0.95)" />
            <span className="mnf-body text-xs italic" style={{ color: "rgba(245,240,232,0.85)" }}>Walk gently, breathe deeply</span>
          </div>
        )}
        {isSaved && (
          <div className="mt-2 flex items-center gap-1.5">
            <Icon name="bookmark" size={12} color="#E5B997" filled />
            <span className="mnf-body text-xs italic" style={{ color: "rgba(229,185,151,0.95)" }}>
              In your library {savedCount > 0 ? `· ${savedCount} saved` : ""}
            </span>
          </div>
        )}
      </ScreenHeader>

      <div className="px-5 pt-5 space-y-4">
        {/* Monthly Garden Walk strip */}
        <GardenWalkStrip />

        {/* Streak / week */}
        <GlassCard padding={18} className="mnf-fade-up">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="mnf-eyebrow">Your week of motion</p>
              <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)" }}>
                4 strong days
              </p>
            </div>
            <PillTag tone="coral"><Icon name="auto_awesome" size={11} /> 12-day streak</PillTag>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {recentDays.map((d, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5">
                <div
                  className="w-full"
                  style={{
                    height: 56,
                    borderRadius: 12,
                    background: d.done
                      ? `linear-gradient(180deg, rgba(192,120,74,${0.15 + d.intensity * 0.4}) 0%, rgba(30,74,53,${0.55 + d.intensity * 0.4}) 100%)`
                      : "rgba(30,74,53,0.06)",
                    position: "relative",
                  }}
                >
                  {d.done && (
                    <Icon
                      name="check"
                      size={14}
                      color="#F5F0E8"
                      className="absolute"
                      style={{ bottom: 4, left: "50%", transform: "translateX(-50%)" }}
                    />
                  )}
                </div>
                <span className="text-[10px] mnf-body" style={{ color: "var(--mnf-tg)", fontWeight: 600 }}>{d.d}</span>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Cycle awareness card */}
        <GlassCard
          padding={18}
          className="mnf-fade-up"
          style={{
            background: "linear-gradient(135deg, rgba(127,119,221,0.12) 0%, rgba(245,240,232,0.7) 100%)",
            border: "1px solid rgba(127,119,221,0.18)",
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="flex items-center justify-center"
              style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--mnf-purpleL)", color: "var(--mnf-purple)" }}
            >
              <Icon name="favorite" size={22} filled />
            </div>
            <div className="flex-1">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)" }}>Cycle day 14 · Ovulatory</p>
              <p className="mnf-serif italic mt-1" style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--mnf-fd)" }}>
                Your strength peaks this week, sister. Lean into heavier loads while honoring your rest.
              </p>
            </div>
          </div>
        </GlassCard>

        {/* Season-aware movement guidance */}
        {(isPregnant || isPostpartum || isNursing) && (
          <GlassCard
            padding={18}
            className="mnf-fade-up"
            style={{
              background: blockedByClearance
                ? "linear-gradient(135deg, rgba(160,120,32,0.16) 0%, rgba(245,240,232,0.7) 100%)"
                : "linear-gradient(135deg, rgba(168,212,184,0.18) 0%, rgba(245,240,232,0.7) 100%)",
              border: blockedByClearance
                ? "1px solid rgba(160,120,32,0.28)"
                : "1px solid rgba(168,212,184,0.32)",
            }}
            data-testid="card-season-guidance"
          >
            <div className="flex items-start gap-3">
              <div
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: "rgba(30,74,53,0.10)",
                  color: "var(--mnf-f)",
                }}
              >
                <Icon name={blockedByClearance ? "self_care" : "spa"} size={22} filled />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)" }}>Your season today</p>
                  <SeasonChip season={season ?? null} size="sm" testId="chip-move-season" />
                </div>
                {blockedByClearance ? (
                  <>
                    <p className="mnf-serif italic mt-1.5" style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--mnf-fd)" }}>
                      Rest is righteous, sister. Your body is still healing.
                    </p>
                    <p className="mnf-body mt-2" style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--mnf-body)" }}>
                      Wait for your provider's clearance before lifting. In the meantime, breath work and gentle walking are your strength.
                    </p>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <PillTag tone="forest">Pelvic floor breathing · 5 min</PillTag>
                      <PillTag tone="fiber">Stroller walk · 15 min</PillTag>
                    </div>
                  </>
                ) : isPregnant ? (
                  <>
                    <p className="mnf-serif italic mt-1.5" style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--mnf-fd)" }}>
                      Your body is doing holy work. Move with awareness, not intensity.
                    </p>
                    <ul className="mnf-body mt-2 space-y-1" style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--mnf-body)" }}>
                      <li>• Skip jumps, sprints, and crunches today</li>
                      <li>• Watch for coning along your midline, ease off if you see it</li>
                      <li>• Pelvic floor breathing before each set</li>
                    </ul>
                    <div className="mt-3">
                      <PillTag tone="forest">Daily · Pelvic floor breathing 5 min</PillTag>
                    </div>
                  </>
                ) : isPostpartum ? (
                  <>
                    <p className="mnf-serif italic mt-1.5" style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--mnf-fd)" }}>
                      You're cleared, sister. Rebuild gently from the core out.
                    </p>
                    <ul className="mnf-body mt-2 space-y-1" style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--mnf-body)" }}>
                      <li>• Diastasis check before crunches or planks</li>
                      <li>• Connect breath to floor, then add load</li>
                      <li>• Skip high-impact for at least 12 weeks postpartum</li>
                    </ul>
                    <div className="mt-3">
                      <PillTag tone="forest">Daily · Pelvic floor breathing 5 min</PillTag>
                    </div>
                  </>
                ) : (
                  <p className="mnf-serif italic mt-1.5" style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--mnf-fd)" }}>
                    Hydrate well today. Your body is feeding two.
                  </p>
                )}
              </div>
            </div>
          </GlassCard>
        )}

        {/* Personalized NASM/GGS session for today */}
        <PersonalizedSessionCard
          session={personalizedSession}
          mesocycle={mesocycle}
          hasProfile={!!profile?.completed}
          profile={profile}
          season={season}
        />

        {/* Today's session (legacy logging UI) */}
        <SectionTitle
          eyebrow="Today's session"
          title="Move with intention"
          trailing={`${completedExercises.length} of ${exercises.length}`}
        />

        <GlassCard padding={0} className="mnf-fade-up">
          {exercises.map((ex, i) => (
            <ExerciseRow
              key={ex.id}
              ex={ex}
              index={i}
              isActive={i === active}
              isLast={i === exercises.length - 1}
              userId={userId}
              workoutId={WORKOUT.id}
              workoutTitle={WORKOUT.title}
              setsToday={setsToday.filter((s) => s.exerciseId === ex.id)}
              onToggle={() => setActive(i === active ? -1 : i)}
            />
          ))}
        </GlassCard>

        <div className="grid grid-cols-2 gap-2.5 mnf-fade-up">
          <Btn variant="secondary" block size="lg">
            <Icon name="pause_circle" size={18} /> Rest
          </Btn>
          <Btn variant="primary" block size="lg">
            <Icon name="play_arrow" size={20} filled /> Continue
          </Btn>
        </div>

        {/* Coach Kiley note */}
        <GlassCard
          padding={18}
          className="mnf-fade-up"
          style={{
            background: "linear-gradient(135deg, rgba(30,74,53,0.06) 0%, rgba(245,240,232,0.65) 100%)",
          }}
        >
          <div className="flex items-start gap-3">
            <Avatar initials="K" size={40} ringColor="rgba(192,120,74,0.6)" />
            <div className="flex-1">
              <p className="mnf-eyebrow">Coach Kiley · before you begin</p>
              <p className="mnf-serif italic mt-1" style={{ fontSize: 14.5, lineHeight: 1.55, color: "var(--mnf-fd)" }}>
                "Strength is built quietly, beloved. Five honest reps in His presence outweigh
                fifteen rushed ones. Breathe, root down, and let the work pray with you."
              </p>
            </div>
          </div>
        </GlassCard>

        <div className="h-2" />
      </div>
    </>
  );
}

/* ----------------------------- Exercise row -------------------------------- */

function ExerciseRow({
  ex, index, isActive, isLast, userId, workoutId, workoutTitle, setsToday, onToggle,
}: {
  ex: Exercise;
  index: number;
  isActive: boolean;
  isLast: boolean;
  userId: string;
  workoutId: string;
  workoutTitle: string;
  setsToday: ReturnType<typeof Object>["prototype"] extends never ? never : Array<{
    id: string; setIndex: number; weightLb: number | null; reps: number | null; holdSec: number | null; isPR: boolean;
  }>;
  onToggle: () => void;
}) {
  const completedCount = setsToday.length;
  const allDone = completedCount >= ex.sets;
  const prescription =
    ex.kind === "hold"
      ? `${ex.sets} \u00d7 ${ex.prescribedHoldSec}s`
      : `${ex.sets} \u00d7 ${ex.prescribedReps}`;
  const weightLabel =
    ex.kind === "weighted" ? `${ex.prescribedWeight} lb`
    : ex.kind === "hold" ? "Hold"
    : "BW";

  return (
    <div
      style={{
        borderBottom: !isLast ? "1px solid rgba(30,74,53,0.06)" : "none",
        background: isActive ? "rgba(192,120,74,0.06)" : "transparent",
      }}
    >
      <button
        onClick={onToggle}
        data-testid={`exercise-${index}`}
        className="w-full text-left flex items-center gap-3 px-4 py-3.5 hover-elevate transition-all"
        style={{ cursor: "pointer" }}
      >
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: 36, height: 36, borderRadius: "50%",
            background: allDone ? "var(--mnf-f)" : isActive ? "var(--mnf-t)" : "rgba(30,74,53,0.08)",
            color: allDone || isActive ? "#F5F0E8" : "var(--mnf-tg)",
          }}
        >
          {allDone ? <Icon name="check" size={18} /> : <span className="mnf-serif" style={{ fontSize: 14, fontWeight: 600 }}>{index + 1}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="mnf-body" style={{ fontSize: 14, fontWeight: 600, color: "var(--mnf-tx)" }}>
              {ex.name}
            </p>
            {completedCount > 0 && completedCount < ex.sets && (
              <span className="mnf-eyebrow" style={{ fontSize: 9, color: "var(--mnf-t)", letterSpacing: "0.08em" }}>
                {completedCount}/{ex.sets}
              </span>
            )}
          </div>
          <p className="text-[11px] mnf-body italic mt-0.5" style={{ color: "var(--mnf-tg)" }}>
            {ex.note}
          </p>
        </div>
        <div className="text-right shrink-0 flex items-center gap-2">
          <div>
            <p className="mnf-serif" style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)" }}>
              {prescription}
            </p>
            <p className="text-[10px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.12em" }}>
              {weightLabel}
            </p>
          </div>
          <Icon
            name={isActive ? "expand_less" : "expand_more"}
            size={18}
            color="var(--mnf-tg)"
          />
        </div>
      </button>

      {isActive && (
        <div className="px-4 pb-4 pt-1 space-y-2">
          {Array.from({ length: ex.sets }).map((_, i) => {
            const setIndex = i + 1;
            const logged = setsToday.find((s) => s.setIndex === setIndex);
            return (
              <SetRow
                key={setIndex}
                ex={ex}
                setIndex={setIndex}
                userId={userId}
                workoutId={workoutId}
                workoutTitle={workoutTitle}
                logged={logged ?? null}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Set row ----------------------------------- */

function SetRow({
  ex, setIndex, userId, workoutId, workoutTitle, logged,
}: {
  ex: Exercise;
  setIndex: number;
  userId: string;
  workoutId: string;
  workoutTitle: string;
  logged: { id: string; weightLb: number | null; reps: number | null; holdSec: number | null; isPR: boolean } | null;
}) {
  const lastSet = useStoreQuery(() => queries.lastSetFor(userId, ex.id, setIndex));
  const initialWeight =
    logged?.weightLb ?? lastSet?.weightLb ?? ex.prescribedWeight ?? 0;
  const initialReps =
    logged?.reps ?? lastSet?.reps ?? ex.prescribedReps ?? 0;
  const initialHold =
    logged?.holdSec ?? lastSet?.holdSec ?? ex.prescribedHoldSec ?? 0;

  const [weight, setWeight] = useState<number>(initialWeight);
  const [reps, setReps] = useState<number>(initialReps);
  const [hold, setHold] = useState<number>(initialHold);

  const isLogged = !!logged;

  function logSet() {
    mutations.logWorkoutSet({
      userId, workoutId, workoutTitle,
      exerciseId: ex.id, exerciseName: ex.name, kind: ex.kind,
      setIndex,
      weightLb: ex.kind === "weighted" ? weight : null,
      reps: ex.kind === "hold" ? null : reps,
      holdSec: ex.kind === "hold" ? hold : null,
    });
  }
  function undo() {
    if (logged) mutations.unlogWorkoutSet(logged.id);
  }

  // Compute delta vs last
  let deltaText: { label: string; tone: "coral" | "forest" | "neutral" } | null = null;
  if (lastSet && !isLogged) {
    if (ex.kind === "weighted" && lastSet.weightLb && weight) {
      const d = weight - lastSet.weightLb;
      if (d > 0) deltaText = { label: `+${d} lb vs last`, tone: "coral" };
      else if (d === 0) deltaText = { label: "matched last", tone: "forest" };
      else deltaText = { label: `${d} lb · gentler`, tone: "neutral" };
    } else if (ex.kind === "hold" && lastSet.holdSec) {
      const d = hold - lastSet.holdSec;
      if (d > 0) deltaText = { label: `+${d}s vs last`, tone: "coral" };
      else if (d === 0) deltaText = { label: "matched last", tone: "forest" };
    }
  }
  if (isLogged && logged?.isPR) {
    deltaText = { label: "Personal record", tone: "coral" };
  } else if (isLogged) {
    deltaText = { label: "Logged", tone: "forest" };
  }

  return (
    <div
      className="flex items-center gap-2"
      style={{
        padding: "10px 12px",
        borderRadius: 14,
        background: isLogged
          ? logged?.isPR
            ? "linear-gradient(135deg, rgba(160,120,32,0.10) 0%, rgba(192,120,74,0.10) 100%)"
            : "rgba(30,74,53,0.05)"
          : "rgba(255,255,255,0.65)",
        border: isLogged
          ? logged?.isPR
            ? "1px solid rgba(229,185,151,0.55)"
            : "1px solid rgba(30,74,53,0.06)"
          : "1px solid rgba(30,74,53,0.10)",
      }}
      data-testid={`set-row-${ex.id}-${setIndex}`}
    >
      <div
        className="flex items-center justify-center shrink-0 mnf-eyebrow"
        style={{
          width: 28, height: 28, borderRadius: 999,
          background: isLogged ? "var(--mnf-f)" : "rgba(30,74,53,0.08)",
          color: isLogged ? "#F5F0E8" : "var(--mnf-tg)",
          fontSize: 10, letterSpacing: "0.08em",
        }}
      >
        {isLogged ? <Icon name="check" size={14} /> : `S${setIndex}`}
      </div>

      {ex.kind === "weighted" && (
        <>
          <NumberStepper
            label="lb"
            value={weight}
            step={2.5}
            min={0}
            max={500}
            disabled={isLogged}
            onChange={setWeight}
            testId={`weight-${ex.id}-${setIndex}`}
          />
          <NumberStepper
            label="reps"
            value={reps}
            step={1}
            min={0}
            max={50}
            disabled={isLogged}
            onChange={setReps}
            testId={`reps-${ex.id}-${setIndex}`}
          />
        </>
      )}
      {ex.kind === "bodyweight" && (
        <>
          <div
            className="mnf-eyebrow flex items-center justify-center"
            style={{
              minWidth: 56, height: 32, borderRadius: 10,
              background: "rgba(168,212,184,0.18)", color: "var(--mnf-f)",
              letterSpacing: "0.08em", fontSize: 9,
            }}
          >
            BW
          </div>
          <NumberStepper
            label="reps"
            value={reps}
            step={1}
            min={0}
            max={50}
            disabled={isLogged}
            onChange={setReps}
            testId={`reps-${ex.id}-${setIndex}`}
          />
        </>
      )}
      {ex.kind === "hold" && (
        <NumberStepper
          label="sec"
          value={hold}
          step={5}
          min={0}
          max={300}
          disabled={isLogged}
          onChange={setHold}
          testId={`hold-${ex.id}-${setIndex}`}
        />
      )}

      <div className="flex-1 min-w-0 text-right">
        {deltaText && (
          <p
            className="mnf-eyebrow truncate"
            style={{
              fontSize: 9, letterSpacing: "0.08em",
              color:
                deltaText.tone === "coral" ? "var(--mnf-t)"
                : deltaText.tone === "forest" ? "var(--mnf-f)"
                : "var(--mnf-tg)",
            }}
          >
            {deltaText.label}
          </p>
        )}
      </div>

      {isLogged ? (
        <button
          onClick={undo}
          aria-label="Undo this set"
          data-testid={`button-undo-${ex.id}-${setIndex}`}
          className="flex items-center justify-center shrink-0"
          style={{
            width: 32, height: 32, borderRadius: 999,
            background: "rgba(30,74,53,0.08)", color: "var(--mnf-tg)",
          }}
        >
          <Icon name="undo" size={14} />
        </button>
      ) : (
        <button
          onClick={logSet}
          aria-label="Log this set"
          data-testid={`button-log-${ex.id}-${setIndex}`}
          className="flex items-center justify-center shrink-0 hover-elevate"
          style={{
            width: 36, height: 32, borderRadius: 999,
            background: "var(--mnf-t)", color: "#F5F0E8",
          }}
        >
          <Icon name="check" size={16} />
        </button>
      )}
    </div>
  );
}

/* --------------------------- Number stepper -------------------------------- */

function NumberStepper({
  label, value, step, min, max, disabled, onChange, testId,
}: {
  label: string; value: number; step: number; min: number; max: number;
  disabled: boolean; onChange: (v: number) => void; testId: string;
}) {
  const dec = () => onChange(Math.max(min, +(value - step).toFixed(1)));
  const inc = () => onChange(Math.min(max, +(value + step).toFixed(1)));
  return (
    <div
      className="flex items-center"
      style={{
        height: 32, borderRadius: 10,
        background: disabled ? "rgba(30,74,53,0.05)" : "#fff",
        border: disabled ? "1px solid rgba(30,74,53,0.06)" : "1px solid rgba(30,74,53,0.12)",
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <button
        onClick={dec}
        disabled={disabled}
        className="flex items-center justify-center"
        style={{ width: 22, height: 32, color: disabled ? "var(--mnf-tg)" : "var(--mnf-fd)" }}
        aria-label={`Decrease ${label}`}
        data-testid={`${testId}-dec`}
      >
        <Icon name="remove" size={14} />
      </button>
      <div className="flex flex-col items-center justify-center px-1" style={{ minWidth: 40 }}>
        <span className="mnf-serif" style={{ fontSize: 14, fontWeight: 600, color: "var(--mnf-fd)", lineHeight: 1, fontVariantNumeric: "tabular-nums" }} data-testid={testId}>
          {value}
        </span>
        <span className="mnf-eyebrow" style={{ fontSize: 8, color: "var(--mnf-tg)", letterSpacing: "0.08em", marginTop: 1 }}>
          {label}
        </span>
      </div>
      <button
        onClick={inc}
        disabled={disabled}
        className="flex items-center justify-center"
        style={{ width: 22, height: 32, color: disabled ? "var(--mnf-tg)" : "var(--mnf-fd)" }}
        aria-label={`Increase ${label}`}
        data-testid={`${testId}-inc`}
      >
        <Icon name="add" size={14} />
      </button>
    </div>
  );
}

/* ----------------------- Monthly Garden Walk strip ------------------------ */

const FRUIT_LABEL_MOVE: Record<string, string> = {
  love: "Love",
  joy: "Joy",
  peace: "Peace",
  patience: "Patience",
  kindness: "Kindness",
  goodness: "Goodness",
  faithfulness: "Faithfulness",
  gentleness: "Gentleness",
  "self-control": "Self-control",
};

/**
 * Compact Garden Walk strip for the Move page header. The Move page is
 * strength-focused, so the strip is intentionally quiet: a tap target,
 * not a competing hero. It picks up the current month's fruit and the
 * sister's walk count.
 */
function GardenWalkStrip() {
  const me = useStoreQuery(queries.currentUser);
  const userId = me?.id ?? null;
  const month = useStoreQuery(queries.currentGardenWalkMonth);
  const stats = useStoreQuery(() =>
    month && userId ? queries.gardenWalkStats(userId, month.id) : null,
  );
  if (!month || !stats) return null;
  const fruitLabel = FRUIT_LABEL_MOVE[month.fruit] ?? month.fruit;

  return (
    <Link href="/garden-walk" className="no-underline block" data-testid="link-move-garden-walk">
      <GlassCard
        padding={14}
        className="flex items-center gap-3"
        style={{
          background: "linear-gradient(135deg, rgba(168,212,184,0.20) 0%, rgba(245,240,232,0.7) 100%)",
          border: "1px solid rgba(90,140,112,0.28)",
          cursor: "pointer",
        }}
      >
        <div
          aria-hidden
          className="shrink-0 inline-flex items-center justify-center"
          style={{
            width: 38,
            height: 38,
            borderRadius: 12,
            background: "var(--mnf-f)",
            color: "#F5F0E8",
            fontSize: 18,
          }}
        >
          {month.hero.primary}
        </div>
        <div className="flex-1 min-w-0">
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)" }}>
            Garden Walk · {fruitLabel}
          </p>
          <p
            className="mnf-body"
            style={{ fontSize: 13, color: "var(--mnf-fd)", fontWeight: 600 }}
            data-testid="text-move-walks-progress"
          >
            {stats.walks} of {stats.target} walks this month
          </p>
        </div>
        <Icon name="chevron_right" size={18} color="var(--mnf-tg)" />
      </GlassCard>
    </Link>
  );
}

/* ----------------------- Personalized session card ----------------------- */

function PersonalizedSessionCard({
  session, mesocycle, hasProfile, profile, season,
}: {
  session: GeneratedSession | null;
  mesocycle: { week: 1 | 2 | 3 | 4; block: "A" | "B"; startedAt: number } | null;
  hasProfile: boolean;
  profile: MovementProfile | null | undefined;
  season: SeasonState | null | undefined;
}) {
  const [whyOpen, setWhyOpen] = useState(false);
  if (!hasProfile) {
    return (
      <GlassCard padding={20} className="mnf-fade-up" style={{ background: "linear-gradient(135deg, rgba(168,212,184,0.18) 0%, rgba(245,240,232,0.7) 100%)", border: "1px solid rgba(168,212,184,0.32)" }} data-testid="card-personalize-cta">
        <div className="flex items-start gap-3">
          <div
            className="flex items-center justify-center shrink-0"
            style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(30,74,53,0.10)", color: "var(--mnf-f)" }}
          >
            <Icon name="auto_awesome" size={20} filled />
          </div>
          <div className="flex-1">
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)" }}>Personalize my training</p>
            <p className="mnf-serif italic mt-1" style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--mnf-fd)" }}>
              Tell Coach Kiley about injuries, goals, and your week, and she'll build a four-week NASM block that holds your compounds and rotates your accessories.
            </p>
            <Link href="/onboarding" data-testid="link-personalize">
              <Btn variant="primary" size="sm" className="mt-3">
                Set up my plan <Icon name="arrow_forward" size={14} />
              </Btn>
            </Link>
          </div>
        </div>
      </GlassCard>
    );
  }

  if (!session) {
    return (
      <GlassCard padding={20} className="mnf-fade-up" data-testid="card-rest-day">
        <div className="flex items-start gap-3">
          <div
            className="flex items-center justify-center shrink-0"
            style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(127,119,221,0.14)", color: "var(--mnf-purple)" }}
          >
            <Icon name="self_care" size={20} filled />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)" }}>Rest day</p>
              {mesocycle && (
                <PillTag tone="purple">Week {mesocycle.week} of 4 · Block {mesocycle.block}</PillTag>
              )}
            </div>
            <p className="mnf-serif italic mt-1" style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--mnf-fd)" }}>
              Today is a rest day, sister. Walk a little, breathe a lot, eat your protein.
            </p>
          </div>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard padding={0} className="mnf-fade-up overflow-hidden" data-testid="card-personalized-session">
      <div
        style={{
          padding: "16px 18px",
          background: "linear-gradient(135deg, var(--mnf-f) 0%, var(--mnf-fd) 100%)",
          color: "#F5F0E8",
        }}
      >
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className="mnf-eyebrow" style={{ color: "rgba(229,185,151,0.95)", letterSpacing: "0.12em" }}>Coach Kiley · personalized</span>
          {mesocycle && (
            <span
              className="mnf-body"
              style={{
                padding: "2px 9px", borderRadius: 999,
                background: "rgba(245,240,232,0.16)", color: "#F5F0E8",
                fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
              }}
            >
              Week {mesocycle.week} of 4
            </span>
          )}
          <span
            className="mnf-body"
            style={{
              padding: "2px 9px", borderRadius: 999,
              background: "rgba(229,185,151,0.22)", color: "#E5B997",
              fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
            }}
          >
            Block {session.accessoryBlock}
          </span>
        </div>
        <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.2 }}>
          {session.focus}
        </p>
        <div className="flex items-center gap-3 mt-2">
          <div className="flex items-center gap-1.5">
            <Icon name="schedule" size={13} color="rgba(245,240,232,0.85)" />
            <span className="mnf-body" style={{ fontSize: 12, color: "rgba(245,240,232,0.9)" }}>{session.durationMin} min</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Icon name="fitness_center" size={13} color="rgba(245,240,232,0.85)" />
            <span className="mnf-body" style={{ fontSize: 12, color: "rgba(245,240,232,0.9)" }}>{session.exercises.length} movements</span>
          </div>
        </div>
      </div>

      {session.guidance && (
        <div
          style={{
            padding: "12px 18px",
            background: "rgba(168,212,184,0.18)",
            borderBottom: "1px solid rgba(30,74,53,0.06)",
          }}
        >
          <p className="mnf-body italic" style={{ fontSize: 12.5, color: "#2C6248", lineHeight: 1.55 }}>
            {session.guidance}
          </p>
        </div>
      )}

      {/* Why these movements? expandable disclosure */}
      <WhyTheseMovementsPanel
        open={whyOpen}
        onToggle={() => setWhyOpen((v) => !v)}
        session={session}
        mesocycle={mesocycle}
        profile={profile}
        season={season}
      />

      <div>
        {session.exercises.map((ex, i) => (
          <PersonalizedExerciseRow key={ex.exerciseId} ex={ex} index={i} isLast={i === session.exercises.length - 1} />
        ))}
      </div>
    </GlassCard>
  );
}

/* --------------------- Why these movements? panel ------------------------ */

function WhyTheseMovementsPanel({
  open, onToggle, session, mesocycle, profile, season,
}: {
  open: boolean;
  onToggle: () => void;
  session: GeneratedSession;
  mesocycle: { week: 1 | 2 | 3 | 4; block: "A" | "B"; startedAt: number } | null;
  profile: MovementProfile | null | undefined;
  season: SeasonState | null | undefined;
}) {
  const activeInjuries = (profile?.injuries ?? []).filter((i) => !i.paused);
  const goalLabels: Record<string, string> = {
    "lose-weight": "feel-good weight loss",
    "build-strength": "build strength",
    "flexibility": "flexibility & mobility",
    "postpartum-recovery": "postpartum recovery",
  };
  const seasonLabel =
    season?.kind === "pregnant" ? "pregnancy (GGS Pre/Postnatal V2)"
    : season?.kind === "postpartum" ? "postpartum recovery (GGS Pre/Postnatal V2)"
    : season?.kind === "nursing" ? "nursing season"
    : null;

  const compoundCount = session.exercises.filter((e) => e.tier === "compound").length;
  const accessoryCount = session.exercises.filter((e) => e.tier === "accessory").length;
  const coreCount = session.exercises.filter((e) => e.tier === "core").length;
  const mobilityCount = session.exercises.filter((e) => e.tier === "mobility").length;

  return (
    <div style={{ borderBottom: "1px solid rgba(30,74,53,0.06)" }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid="button-why-these-movements"
        className="flex items-center gap-2 w-full transition-all hover-elevate"
        style={{
          padding: "11px 18px",
          background: "rgba(245,240,232,0.6)",
          color: "var(--mnf-fd)",
          textAlign: "left",
          cursor: "pointer",
          border: "none",
        }}
      >
        <Icon name="info" size={15} color="var(--mnf-f)" />
        <span className="mnf-body" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--mnf-f)", letterSpacing: "0.01em" }}>
          Why these movements?
        </span>
        <span style={{ flex: 1 }} />
        <Icon name={open ? "expand_less" : "expand_more"} size={18} color="var(--mnf-f)" />
      </button>
      <div
        style={{
          maxHeight: open ? 600 : 0,
          overflow: "hidden",
          transition: "max-height 280ms cubic-bezier(0.22, 1, 0.36, 1)",
          background: "rgba(168,212,184,0.10)",
        }}
      >
        <div style={{ padding: "14px 18px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {mesocycle && (
            <ReasoningRow
              icon="calendar_month"
              label="Mesocycle"
              body={`Week ${mesocycle.week} of 4, Block ${mesocycle.block}. Compounds (${compoundCount}) hold for the full four-week block so we can layer progressive overload week over week. Accessories (${accessoryCount}) rotate every two weeks, which is why you're in Block ${mesocycle.block} right now.`}
            />
          )}
          {profile && profile.goals.length > 0 && (
            <ReasoningRow
              icon="flag"
              label="Your goals"
              body={`Tuned for ${profile.goals.map((g) => goalLabels[g] ?? g).join(", ")}. The ${session.focus.toLowerCase()} focus today balances strength work with ${coreCount} core and ${mobilityCount} mobility piece${mobilityCount === 1 ? "" : "s"} to keep you moving well, not just hard.`}
            />
          )}
          {activeInjuries.length > 0 && (
            <ReasoningRow
              icon="healing"
              label="Active injury flags"
              body={`Routing around ${activeInjuries.map((i) => i.flag.replace("-", " ")).join(", ")}. Coach Kiley substitutes joint-friendly variations (think tempo, range, and load tweaks) so you keep training without flaring anything.`}
            />
          )}
          {seasonLabel && (
            <ReasoningRow
              icon="favorite"
              label="Season"
              body={`Layered with ${seasonLabel}. Movements are gated and dosed for this season, with extra attention to pelvic floor, breathing, and load progression.`}
            />
          )}
          <ReasoningRow
            icon="auto_awesome"
            label="Programming style"
            body="NASM Optimum Performance Training: a four-week compound hold with two-week accessory rotation, progressive overload week to week. Built for sustainable strength, not all-out workouts."
          />
        </div>
      </div>
    </div>
  );
}

function ReasoningRow({ icon, label, body }: { icon: string; label: string; body: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <div
        className="flex items-center justify-center shrink-0"
        style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(30,74,53,0.10)", color: "var(--mnf-f)", marginTop: 1 }}
      >
        <Icon name={icon} size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)", fontSize: 9.5, letterSpacing: "0.10em" }}>{label}</p>
        <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-fd)", lineHeight: 1.5, marginTop: 1 }}>
          {body}
        </p>
      </div>
    </div>
  );
}

function PersonalizedExerciseRow({
  ex, index, isLast,
}: {
  ex: SessionExercise;
  index: number;
  isLast: boolean;
}) {
  const tierTone: Record<SessionExercise["tier"], "forest" | "coral" | "purple" | "fiber"> = {
    compound: "forest",
    accessory: "coral",
    core: "purple",
    mobility: "fiber",
  };
  const prescription = ex.holdSec
    ? `${ex.sets} \u00d7 ${ex.holdSec}s`
    : ex.reps
      ? `${ex.sets} \u00d7 ${ex.reps}`
      : `${ex.sets} sets`;
  const loadLabel = ex.weight
    ? `${ex.weight} lb`
    : ex.equipment === "bodyweight"
      ? "BW"
      : ex.holdSec
        ? "Hold"
        : ex.equipment.charAt(0).toUpperCase() + ex.equipment.slice(1);

  return (
    <div
      style={{
        padding: "14px 18px",
        borderBottom: !isLast ? "1px solid rgba(30,74,53,0.06)" : "none",
      }}
      data-testid={`personalized-exercise-${index}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: 30, height: 30, borderRadius: "50%",
            background: "rgba(30,74,53,0.08)", color: "var(--mnf-f)",
          }}
        >
          <span className="mnf-serif" style={{ fontSize: 13, fontWeight: 600 }}>{index + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="mnf-body" style={{ fontSize: 14, fontWeight: 700, color: "var(--mnf-fd)" }}>
              {ex.name}
            </p>
            <PillTag tone={tierTone[ex.tier]}>{ex.tier}</PillTag>
          </div>
          <p className="mnf-body italic mt-0.5" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.45 }}>
            {ex.cue}
          </p>
          {ex.progressionNote && (
            <p className="mnf-body mt-1" style={{ fontSize: 11, color: "var(--mnf-t)", letterSpacing: "0.02em" }}>
              {ex.progressionNote}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="mnf-serif" style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)" }}>
            {prescription}
          </p>
          <p className="mnf-body uppercase" style={{ fontSize: 9.5, color: "var(--mnf-tg)", letterSpacing: "0.12em", fontWeight: 600 }}>
            {loadLabel}
          </p>
        </div>
      </div>
    </div>
  );
}
